import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { ConditionEvaluationObject } from '@unifyapps/defs/types/conditionEvaluationObject';
import type { ConditionalPath, DynamicPath } from '@unifyapps/defs/types/page';
import get from 'lodash/get';
import { DependencyHelper } from '../../../dependency/helpers/DependencyHelper';
import { setIn } from '../../../immutable';
import { isConditionalOperator } from '../../context/DependencyGraphProvider/handlers';
import type { ComputeContextType } from '../computeContext/context/types';
import {
  type UseGetNoCodeComputeDataType,
  useGetNoCodeComputedData,
} from '../computeContext/useGetNoCodeComputedData';
import {
  type UseEvaluateConditionalOperator,
  useEvaluateConditionalOperator,
} from '../useComputeConditionEvaluationGetter';

export interface ComputeBlockStateProps {
  blockState: BlockStateUnionType;
  context: ComputeContextType;
  dynPaths?: DynamicPath[];
  condPaths?: ConditionalPath[];
  getNoCodeBulkComputedData: UseGetNoCodeComputeDataType['getNoCodeBulkComputedData'];
  evaluateConditionalOperator: UseEvaluateConditionalOperator['evaluateConditionalOperator'];
}

export function evalutedDynamicPaths(
  blockState: unknown,
  dynPaths: DynamicPath[],
  context: ComputeContextType,
  getNoCodeBulkComputedData: ComputeBlockStateProps['getNoCodeBulkComputedData'],
): Record<string, unknown> | undefined {
  if (dynPaths.length === 0) return;

  const dynamicPropertyPaths = DependencyHelper.getPathsFromDynamicPaths(dynPaths);
  if (!dynamicPropertyPaths?.length) return;

  const bulkExpressions: Record<string, string> = {};
  let hasExpressions = false;

  for (const path of dynamicPropertyPaths) {
    const value = get(blockState, path) as string | undefined;

    if (typeof value === 'string') {
      bulkExpressions[path] = value;
      hasExpressions = true;
    } else {
      console.error(
        `Dynamic property not found at a path: ${path}`,
        (blockState as BlockStateUnionType).id,
        ` value is: ${value}`,
      );
    }
  }

  if (hasExpressions) {
    return getNoCodeBulkComputedData(bulkExpressions, context);
  }
}

function evaluateConditionalPaths<T>(
  computedBlockState: Record<string, unknown>,
  condPaths: ConditionalPath[],
  context: ComputeContextType,
  evaluateConditionalOperator: ComputeBlockStateProps['evaluateConditionalOperator'],
) {
  if (condPaths.length === 0) return computedBlockState as T;

  const conditionalOperatorPaths = DependencyHelper.getPathsFromConditionalPaths(condPaths);
  if (!conditionalOperatorPaths?.length) return computedBlockState as T;

  let newComputedBlockState = { ...computedBlockState };

  for (const path of conditionalOperatorPaths) {
    const value = get(newComputedBlockState, path) as ConditionEvaluationObject | undefined;

    if (isConditionalOperator('conditions', value)) {
      const evaluatedValue = evaluateConditionalOperator({
        conditionEvaluationObj: value,
        context,
        isPreComputed: true,
      });
      newComputedBlockState = setIn(newComputedBlockState, path, evaluatedValue);
    } else if (path !== 'visibility.conditions') {
      console.error(`ConditionalOperator not found at path: ${path}`);
    }
  }

  return newComputedBlockState as T;
}

export function getComputedBlockState<T>(props: ComputeBlockStateProps) {
  const {
    blockState,
    context,
    dynPaths = [],
    condPaths = [],
    getNoCodeBulkComputedData,
    evaluateConditionalOperator,
  } = props;

  if (dynPaths.length === 0 && condPaths.length === 0) {
    return blockState as T;
  }

  let newComputedBlockState = { ...blockState };

  const evaluatedDynamicPaths = evalutedDynamicPaths(
    blockState,
    dynPaths,
    context,
    getNoCodeBulkComputedData,
  );

  for (const dynPath of dynPaths) {
    const path = dynPath.p;
    const value = evaluatedDynamicPaths?.[dynPath.p];
    const valueAtDynamicPath = get(newComputedBlockState, path);

    // because we do not compute dynamic paths that do not have their value as a string, we need to add a check here as well
    // check line 48, this is because evaluatedDynamicPaths can
    // result in path value being undefined either because it is undefined or either because we didn't compute it all
    if (typeof valueAtDynamicPath === 'string') {
      newComputedBlockState = setIn(newComputedBlockState, path, value);
    } else {
      console.error(`Dynamic path value is not a string at path: ${path}`);
    }
  }

  return evaluateConditionalPaths<T>(
    newComputedBlockState,
    condPaths,
    context,
    evaluateConditionalOperator,
  );
}

export interface UseComputeBlockStateProps {
  blockState: BlockStateUnionType;
  context: ComputeContextType;
  dynPaths?: DynamicPath[];
  condPaths?: ConditionalPath[];
}

export function useComputeBlockStateFromPaths<T>(props: UseComputeBlockStateProps) {
  const { getNoCodeBulkComputedData } = useGetNoCodeComputedData();
  const { evaluateConditionalOperator } = useEvaluateConditionalOperator();

  return getComputedBlockState<T>({
    ...props,
    getNoCodeBulkComputedData,
    evaluateConditionalOperator,
  });
}
